import { checkProjectsUpdates } from 'common/services/pdt.service';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { teamProjectSelector } from 'state/selectors/projects';
import _ from 'lodash';
import { PROJECT_STATUS } from '@linearb/linjs-common';
import useTeamById from 'state/selectors/hooks/useTeamById';

const POLLING_DURATION_MS = 60 * 1000;

const useCheckTeamPDTStatus = teamId => {
  const [teamPdtStatus, setTeamPdtStatus] = useState(null);
  const [loadingPDTProject, setLoadingPDTProject] = useState(false);

  const team = useTeamById(teamId);
  const teamProject = useSelector(state =>
    teamProjectSelector(state, team?.id)
  );
  const pdtProjectId = _.get(teamProject, 'pdt_project_id');

  const intervalRef = useRef(null); // Ref to store the interval ID
  const previousPdtProjectIdRef = useRef(pdtProjectId); // To track if the team has changed

  // Cleanup previous polling when team changes
  useEffect(() => {
    if (pdtProjectId !== previousPdtProjectIdRef.current) {
      // If pdtProjectId changes, reset state and stop the current polling interval
      setTeamPdtStatus(null);
      setLoadingPDTProject(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      previousPdtProjectIdRef.current = pdtProjectId;
    }
  }, [pdtProjectId]);

  const checkProjectStatus = async () => {
    if (!pdtProjectId) return;
    setLoadingPDTProject(true);
    try {
      const result = await checkProjectsUpdates({
        projects_ids: [pdtProjectId],
        duration_filter: 'I',
        fetch_data: false
      });
      setTeamPdtStatus(result?.[0]?.status ?? null);
    } catch {
      setTeamPdtStatus(null);
    } finally {
      setLoadingPDTProject(false);
    }
  };

  // Polling logic to check project status periodically
  useEffect(() => {
    if (!pdtProjectId) return;

    if (teamPdtStatus !== PROJECT_STATUS.STEADY) {
      setLoadingPDTProject(true);
      checkProjectStatus();

      intervalRef.current = setInterval(() => {
        if (teamPdtStatus !== PROJECT_STATUS.STEADY) {
          checkProjectStatus();
        } else {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, POLLING_DURATION_MS);
    }

    // Cleanup the interval if exists on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [pdtProjectId, teamPdtStatus]);

  return { loadingPDTProject, teamPdtStatus };
};

export default useCheckTeamPDTStatus;
